import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { RefreshCw, Save, Search, X } from "lucide-react";

const STORAGE_KEY = "tulocalmx_admin_panel_key";

const statusOptions = [
  ["publicada", "Publicada"],
  ["pendiente_revision", "Pendiente"],
  ["requiere_revision_manual", "Revision manual"],
  ["pausada", "Pausada"],
  ["rechazada", "Rechazada"],
  ["eliminada", "Eliminada"],
];

const typeOptions = [
  ["local", "Local"],
  ["bodega", "Bodega"],
  ["terreno", "Terreno"],
  ["oficina", "Oficina / consultorio"],
  ["casa", "Casa para negocio"],
  ["economico", "Accesible"],
];

const emptyProperty = {
  id: "",
  estado_publicacion: "pendiente_revision",
  plan: "gratis",
  pago_estado: "pendiente",
  tipo_anunciante: "dueno",
  nombre: "",
  telefono: "",
  correo: "",
  precio: "",
  ubicacion: "",
  calle: "",
  numero: "",
  colonia: "",
  ciudad: "",
  codigo_postal: "",
  lat: "",
  lng: "",
  map_url: "",
  tipo: "local",
  descripcion: "",
  ventajas: "",
  frente: "",
  fondo: "",
  metros: "",
  altura: "",
  nivel: "",
  estacionamiento: "",
  servicios: "",
  disponibilidad: "",
  contrato: "Mínimo 1 año",
  zona_tipo: "",
  restricciones: "",
  negocios_misma_plaza: "",
  ayuda_tulocalmx: false,
  contacto_directo: "",
  comision_tulocalmx: "",
  medidas: "",
  imagenes: [],
  videos: [],
  planos: [],
  revision_score: 0,
  revision_alertas: [],
  posible_duplicado: false,
  duplicado_grupo_id: "",
  publicada_en: "",
  rechazada_en: "",
  notas_admin: "",
};

const formatMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "$0";
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(amount);
};

const linesToArray = (value) =>
  String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const arrayToLines = (value) => (Array.isArray(value) ? value.join("\n") : "");

const uploadFiles = async (files, resourceType = "image") => {
  const urls = [];

  for (const file of files) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "tulocalmx_upload");

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/dz2ylmtpr/${resourceType}/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error("No se pudo subir un archivo.");
    }

    const data = await response.json();
    urls.push(data.secure_url);
  }

  return urls;
};

const TextField = ({ label, value, onChange, type = "text", className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-black uppercase tracking-wide text-slate-500">
      {label}
    </span>
    <input
      type={type}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-400"
    />
  </label>
);

const TextArea = ({ label, value, onChange, rows = 3, help }) => (
  <label className="block">
    <span className="text-xs font-black uppercase tracking-wide text-slate-500">
      {label}
    </span>
    {help && <span className="mt-1 block text-xs text-slate-500">{help}</span>}
    <textarea
      rows={rows}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-400"
    />
  </label>
);

const SelectField = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="text-xs font-black uppercase tracking-wide text-slate-500">
      {label}
    </span>
    <select
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-400"
    >
      {options.map(([optionValue, labelText]) => (
        <option key={optionValue} value={optionValue}>
          {labelText}
        </option>
      ))}
    </select>
  </label>
);

const AdminPropiedades = () => {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyProperty);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [newImages, setNewImages] = useState([]);
  const [newVideos, setNewVideos] = useState([]);
  const [newPlans, setNewPlans] = useState([]);
  const noticeRef = useRef(null);

  const selectedProperty = useMemo(
    () => rows.find((row) => row.id === selectedId),
    [rows, selectedId]
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      [
        row.nombre,
        row.telefono,
        row.correo,
        row.ubicacion,
        row.colonia,
        row.tipo,
        row.estado_publicacion,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [rows, search]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectProperty = (property) => {
    setSelectedId(property.id);
    setForm({
      ...emptyProperty,
      ...property,
      precio: property.precio ?? "",
      lat: property.lat ?? "",
      lng: property.lng ?? "",
      revision_alertas: Array.isArray(property.revision_alertas)
        ? property.revision_alertas
        : [],
      imagenes: Array.isArray(property.imagenes) ? property.imagenes : [],
      videos: Array.isArray(property.videos) ? property.videos : [],
      planos: Array.isArray(property.planos) ? property.planos : [],
      duplicado_grupo_id: property.duplicado_grupo_id || "",
      publicada_en: property.publicada_en || "",
      rechazada_en: property.rechazada_en || "",
    });
    setNewImages([]);
    setNewVideos([]);
    setNewPlans([]);
    setMessage("");
    setError("");
  };

  const loadProperties = async () => {
    if (!key.trim()) {
      setError("Escribe tu clave admin.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      setMessage("");
      localStorage.setItem(STORAGE_KEY, key.trim());

      const response = await fetch(
        `/api/admin-pro-alerts?mode=properties&key=${encodeURIComponent(key.trim())}`,
        { cache: "no-store" }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || data.error || "No se pudo cargar.");
      }

      const nextRows = Array.isArray(data.rows) ? data.rows : [];
      setRows(nextRows);

      if (nextRows.length && !selectedId) {
        selectProperty(nextRows[0]);
      } else if (selectedId) {
        const refreshed = nextRows.find((row) => row.id === selectedId);
        if (refreshed) selectProperty(refreshed);
      }
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (key) loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProperty = async (event) => {
    event.preventDefault();
    if (!form.id) return;

    try {
      setIsSaving(true);
      setError("");
      setMessage("");

      const uploadedImages = await uploadFiles(newImages, "image");
      const uploadedVideos = await uploadFiles(newVideos, "video");
      const uploadedPlans = await uploadFiles(newPlans, "auto");
      const imagenes = [...(form.imagenes || []), ...uploadedImages].slice(0, 10);
      const videos = [...(form.videos || []), ...uploadedVideos].slice(0, 4);
      const planos = [...(form.planos || []), ...uploadedPlans].slice(0, 6);

      const payload = {
        ...form,
        mode: "properties",
        key: key.trim(),
        imagenes,
        videos,
        planos,
        revision_alertas: linesToArray(arrayToLines(form.revision_alertas)),
        posible_duplicado: Boolean(form.posible_duplicado),
        ayuda_tulocalmx: Boolean(form.ayuda_tulocalmx),
      };

      const response = await fetch("/api/admin-pro-alerts", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": key.trim(),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || data.error || "No se pudo guardar.");
      }

      const savedRow = data.row;
      setRows((prev) =>
        prev.map((row) => (row.id === savedRow.id ? savedRow : row))
      );
      selectProperty(savedRow);
      const savedAddress = [
        savedRow.calle,
        savedRow.numero,
        savedRow.colonia,
        savedRow.ciudad,
      ]
        .filter(Boolean)
        .join(", ");
      setMessage(
        savedRow.estado_publicacion === "publicada"
          ? `Propiedad guardada y publicada. Direccion confirmada: ${savedAddress || savedRow.ubicacion || "sin direccion separada"}.`
          : `Propiedad guardada como ${savedRow.estado_publicacion}. Direccion confirmada: ${savedAddress || savedRow.ubicacion || "sin direccion separada"}.`
      );
      window.setTimeout(() => {
        noticeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  const updateStatus = (nextStatus) => {
    updateForm("estado_publicacion", nextStatus);
  };

  const setArrayFromLines = (field, value) => {
    updateForm(field, linesToArray(value));
  };

  const removeMedia = (field, indexToRemove) => {
    updateForm(
      field,
      (form[field] || []).filter((_, index) => index !== indexToRemove)
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <Header />
      <main className="mx-auto max-w-7xl px-4 pb-12">
        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-orange-500">
              Panel admin
            </p>
            <h1 className="text-2xl font-black text-slate-950">
              Editar propiedades
            </h1>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Revisa, corrige, publica, pausa o rechaza cualquier propiedad enviada.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500"
            aria-label="Salir"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mb-5 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:grid-cols-[1fr_auto]">
          <input
            type="password"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder="Clave admin"
            className="rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
          />
          <button
            type="button"
            onClick={loadProperties}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0056D2] px-4 py-3 text-sm font-black text-white disabled:bg-blue-300"
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            {isLoading ? "Cargando..." : "Cargar propiedades"}
          </button>
        </div>

        <div ref={noticeRef} />

        {error && (
          <div className="mb-5 rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-800">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-5 rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">
            {message}
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
          <aside className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por nombre, telefono, colonia..."
                  className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-orange-400"
                />
              </div>
              <p className="mt-2 text-xs font-bold text-slate-500">
                {filteredRows.length} propiedades
              </p>
            </div>

            <div className="max-h-[760px] divide-y divide-slate-100 overflow-y-auto">
              {filteredRows.map((property) => (
                <button
                  key={property.id}
                  type="button"
                  onClick={() => selectProperty(property)}
                  className={`grid w-full grid-cols-[72px_1fr] gap-3 p-3 text-left transition hover:bg-slate-50 ${
                    property.id === selectedId ? "bg-orange-50" : ""
                  }`}
                >
                  <img
                    src={property.imagenes?.[0] || ""}
                    alt=""
                    className="h-16 w-16 rounded-lg bg-slate-100 object-cover"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black text-slate-950">
                      {property.ubicacion || "Sin ubicacion"}
                    </span>
                    <span className="mt-1 block text-xs font-semibold text-slate-500">
                      {property.tipo} · {formatMoney(property.precio)}
                    </span>
                    <span className="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black uppercase text-slate-600">
                      {property.estado_publicacion}
                    </span>
                  </span>
                </button>
              ))}

              {!filteredRows.length && (
                <p className="p-5 text-sm font-semibold text-slate-500">
                  No hay propiedades para mostrar.
                </p>
              )}
            </div>
          </aside>

          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            {!selectedProperty ? (
              <div className="flex min-h-[360px] items-center justify-center text-center">
                <div>
                  <p className="font-black text-slate-950">
                    Carga y selecciona una propiedad.
                  </p>
                  <p className="mt-2 text-sm text-slate-500">
                    Aqui aparecera el editor completo.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={saveProperty} className="space-y-6">
                <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500">
                      Editando
                    </p>
                    <h2 className="text-xl font-black text-slate-950">
                      {form.ubicacion || "Propiedad sin ubicacion"}
                    </h2>
                    <p className="mt-1 text-xs text-slate-500">ID: {form.id}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/propiedad/supabase-${form.id}`}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-black text-[#0056D2]"
                    >
                      Ver ficha
                    </Link>
                    <button type="button" onClick={() => updateStatus("publicada")} className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-black text-white">
                      Marcar publicada
                    </button>
                    <button type="button" onClick={() => updateStatus("pausada")} className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-black text-slate-700">
                      Pausar
                    </button>
                    <button type="button" onClick={() => updateStatus("rechazada")} className="rounded-lg bg-red-50 px-3 py-2 text-sm font-black text-red-700">
                      Rechazar
                    </button>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <SelectField label="Estado" value={form.estado_publicacion} onChange={(value) => updateForm("estado_publicacion", value)} options={statusOptions} />
                  <SelectField label="Tipo" value={form.tipo} onChange={(value) => updateForm("tipo", value)} options={typeOptions} />
                  <TextField label="Precio" type="number" value={form.precio} onChange={(value) => updateForm("precio", value)} />
                  <TextField label="Plan" value={form.plan} onChange={(value) => updateForm("plan", value)} />
                  <TextField label="Pago" value={form.pago_estado} onChange={(value) => updateForm("pago_estado", value)} />
                  <TextField label="Tipo anunciante" value={form.tipo_anunciante} onChange={(value) => updateForm("tipo_anunciante", value)} />
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <TextField label="Nombre" value={form.nombre} onChange={(value) => updateForm("nombre", value)} />
                  <TextField label="WhatsApp" value={form.telefono} onChange={(value) => updateForm("telefono", value)} />
                  <TextField label="Correo" value={form.correo} onChange={(value) => updateForm("correo", value)} />
                </div>

                <div className="rounded-xl bg-slate-50 p-4">
                  <h3 className="mb-3 font-black text-slate-950">Ubicacion</h3>
                  <div className="grid gap-4 lg:grid-cols-3">
                    <TextField label="Ubicacion visible" value={form.ubicacion} onChange={(value) => updateForm("ubicacion", value)} className="lg:col-span-3" />
                    <TextField label="Calle" value={form.calle} onChange={(value) => updateForm("calle", value)} />
                    <TextField label="Numero" value={form.numero} onChange={(value) => updateForm("numero", value)} />
                    <TextField label="Colonia" value={form.colonia} onChange={(value) => updateForm("colonia", value)} />
                    <TextField label="Ciudad" value={form.ciudad} onChange={(value) => updateForm("ciudad", value)} />
                    <TextField label="Codigo postal" value={form.codigo_postal} onChange={(value) => updateForm("codigo_postal", value)} />
                    <TextField label="Latitud" value={form.lat} onChange={(value) => updateForm("lat", value)} />
                    <TextField label="Longitud" value={form.lng} onChange={(value) => updateForm("lng", value)} />
                    <TextField label="Mapa / URL" value={form.map_url} onChange={(value) => updateForm("map_url", value)} className="lg:col-span-2" />
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-3">
                  <TextField label="Frente" value={form.frente} onChange={(value) => updateForm("frente", value)} />
                  <TextField label="Fondo" value={form.fondo} onChange={(value) => updateForm("fondo", value)} />
                  <TextField label="Metros" value={form.metros} onChange={(value) => updateForm("metros", value)} />
                  <TextField label="Altura" value={form.altura} onChange={(value) => updateForm("altura", value)} />
                  <TextField label="Nivel" value={form.nivel} onChange={(value) => updateForm("nivel", value)} />
                  <TextField label="Estacionamiento" value={form.estacionamiento} onChange={(value) => updateForm("estacionamiento", value)} />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <TextArea label="Servicios" value={form.servicios} onChange={(value) => updateForm("servicios", value)} rows={3} />
                  <TextArea label="Disponibilidad" value={form.disponibilidad} onChange={(value) => updateForm("disponibilidad", value)} rows={3} />
                  <TextField label="Contrato" value={form.contrato} onChange={(value) => updateForm("contrato", value)} />
                  <TextField label="Zona" value={form.zona_tipo} onChange={(value) => updateForm("zona_tipo", value)} />
                  <TextArea label="Restricciones" value={form.restricciones} onChange={(value) => updateForm("restricciones", value)} rows={4} />
                  <TextArea label="Entorno / negocios cercanos" value={form.negocios_misma_plaza} onChange={(value) => updateForm("negocios_misma_plaza", value)} rows={4} />
                </div>

                <TextArea label="Descripcion" value={form.descripcion} onChange={(value) => updateForm("descripcion", value)} rows={5} />
                <TextArea label="Ventajas principales" help="Una ventaja por renglon. La ficha las mostrara como lista ordenada." value={form.ventajas} onChange={(value) => updateForm("ventajas", value)} rows={5} />
                <TextArea label="Medidas guardadas" value={form.medidas} onChange={(value) => updateForm("medidas", value)} rows={4} />

                <div className="grid gap-4 lg:grid-cols-2">
                  <TextField label="Contacto directo" value={form.contacto_directo} onChange={(value) => updateForm("contacto_directo", value)} />
                  <TextField label="Comision Tulocalmx" value={form.comision_tulocalmx} onChange={(value) => updateForm("comision_tulocalmx", value)} />
                  <TextField label="Revision score" type="number" value={form.revision_score} onChange={(value) => updateForm("revision_score", Number(value || 0))} />
                  <TextField label="Grupo duplicado" value={form.duplicado_grupo_id} onChange={(value) => updateForm("duplicado_grupo_id", value)} />
                  <TextField label="Publicada en" value={form.publicada_en} onChange={(value) => updateForm("publicada_en", value)} />
                  <TextField label="Rechazada en" value={form.rechazada_en} onChange={(value) => updateForm("rechazada_en", value)} />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm font-bold">
                    <input type="checkbox" checked={Boolean(form.ayuda_tulocalmx)} onChange={(event) => updateForm("ayuda_tulocalmx", event.target.checked)} />
                    Ayuda Tulocalmx
                  </label>
                  <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-sm font-bold">
                    <input type="checkbox" checked={Boolean(form.posible_duplicado)} onChange={(event) => updateForm("posible_duplicado", event.target.checked)} />
                    Posible duplicado
                  </label>
                </div>

                <TextArea label="Alertas de revision" help="Una alerta por renglon." value={arrayToLines(form.revision_alertas)} onChange={(value) => setArrayFromLines("revision_alertas", value)} rows={4} />
                <TextArea label="Notas admin" value={form.notas_admin} onChange={(value) => updateForm("notas_admin", value)} rows={4} />

                <div className="grid gap-5 lg:grid-cols-3">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <h3 className="font-black text-slate-950">Fotos</h3>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {(form.imagenes || []).map((image, index) => (
                        <div key={`${image}-${index}`} className="relative aspect-square overflow-hidden rounded-lg bg-slate-100">
                          <button type="button" onClick={() => removeMedia("imagenes", index)} className="absolute right-1 top-1 rounded-full bg-black/70 px-2 py-1 text-xs text-white">
                            x
                          </button>
                          <img src={image} alt="" className="h-full w-full object-cover" />
                        </div>
                      ))}
                    </div>
                    <TextArea label="Links de fotos" help="Un link por renglon." value={arrayToLines(form.imagenes)} onChange={(value) => setArrayFromLines("imagenes", value)} rows={5} />
                    <input type="file" accept="image/*" multiple onChange={(event) => setNewImages(Array.from(event.target.files || []))} className="mt-3 w-full rounded-lg border border-slate-200 p-2 text-sm" />
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <h3 className="font-black text-slate-950">Videos</h3>
                    <TextArea label="Links de videos" help="Un link por renglon." value={arrayToLines(form.videos)} onChange={(value) => setArrayFromLines("videos", value)} rows={9} />
                    <input type="file" accept="video/*" multiple onChange={(event) => setNewVideos(Array.from(event.target.files || []))} className="mt-3 w-full rounded-lg border border-slate-200 p-2 text-sm" />
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <h3 className="font-black text-slate-950">Planos / documentos</h3>
                    <TextArea label="Links de planos" help="Un link por renglon." value={arrayToLines(form.planos)} onChange={(value) => setArrayFromLines("planos", value)} rows={9} />
                    <input type="file" accept="image/*,.pdf" multiple onChange={(event) => setNewPlans(Array.from(event.target.files || []))} className="mt-3 w-full rounded-lg border border-slate-200 p-2 text-sm" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSaving}
                  className="sticky bottom-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F97316] px-5 py-4 font-black text-white shadow-lg disabled:bg-orange-300"
                >
                  <Save size={18} />
                  {isSaving ? "Guardando..." : "Guardar todos los cambios"}
                </button>
              </form>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default AdminPropiedades;
